import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { validDocumentTypeForInsurance } from '../utils/documentMaps';

/**
 * Extracts document types from passengers.
 * Prioritizes form values over purchase passengers.
 *
 * @param {object} [passengersForm] - Redux form object for passengers
 * @param {object} [purchase] - Purchase object containing current passengers
 * @returns {string[]} - Array of passenger document types
 */
const getPassengerDocuments = (passengersForm, purchase) => {
  const formPassengers = passengersForm?.values?.passengers;
  const purchasePassengers = purchase?.passengers;

  const passengerSources = formPassengers || purchasePassengers || [];
  return passengerSources.map((p) => p.documentType).filter(Boolean);
};

/**
 * Extracts purchaser document type.
 * Prioritizes form values over purchaser object.
 *
 * @param {object} [purchaserForm] - Redux form object for purchaser
 * @param {object} [purchaser] - Purchaser object from props
 * @returns {string|null} - Purchaser document type or null
 */
const getPurchaserDocument = (purchaserForm, purchaser) =>
  purchaserForm?.values?.documentType || purchaser?.documentType || null;

/**
 * Checks if any document in the array is ineligible for insurance.
 *
 * @param {string[]} documents - Array of document types
 * @returns {boolean} - True if any document is ineligible
 */
const hasInvalidInsuranceDocument = (documents) =>
  documents.some((docType) => validDocumentTypeForInsurance(docType) === false);

/**
 * useInsuranceEligibility hook.
 * Calculate insurance eligibility based on passenger and purchaser document types.
 * Uses memoization to avoid recalculating on every render.
 *
 * @param {object} params - Configuration object
 * @param {object} [params.passengersForm] - Redux form object for passengers
 * @param {object} [params.purchaserForm] - Redux form object for purchaser
 * @param {object} [params.purchaser] - Purchaser object from props
 * @param {object} [params.purchase] - Purchase object containing current passengers
 * @returns {object} - Returns object with hasIneligibleDoc (boolean) and passengerDocs (array)
 */
export const useInsuranceEligibility = ({ passengersForm, purchaserForm, purchaser, purchase }) => {
  return useMemo(() => {
    const passengerDocs = getPassengerDocuments(passengersForm, purchase);
    const purchaserDoc = getPurchaserDocument(purchaserForm, purchaser);

    const allDocs = purchaserDoc ? [...passengerDocs, purchaserDoc] : passengerDocs;

    return {
      hasIneligibleDoc: hasInvalidInsuranceDocument(allDocs),
      passengerDocs: allDocs,
    };
  }, [passengersForm, purchaserForm, purchaser, purchase]);
};

/**
 * withInsuranceEligibility HOC.
 * Higher Order Component that injects insurance eligibility data as a prop.
 * This allows class components to use the useInsuranceEligibility hook.
 *
 * @param {React.Component} WrappedComponent - The component to wrap
 * @returns {React.Component} - Enhanced component with insuranceEligibility prop
 *
 * @example
 * class MyComponent extends Component {
 *   render() {
 *     const { insuranceEligibility } = this.props;
 *     const { hasIneligibleDoc } = insuranceEligibility;
 *     // ...
 *   }
 * }
 * export default withInsuranceEligibility(MyComponent);
 */
export const withInsuranceEligibility = (WrappedComponent) => {
  /**
   * WithInsuranceEligibility component.
   * Wrap a component and inject insurance eligibility data.
   *
   * @param {object} props - All component props.
   * @returns {React.Element} - The wrapped component with insuranceEligibility prop.
   */
  const WithInsuranceEligibility = (props) => {
    const { passengersForm, purchaserForm, purchaser, purchase } = props;

    const insuranceEligibility = useInsuranceEligibility({
      passengersForm,
      purchaserForm,
      purchaser,
      purchase,
    });

    return React.createElement(WrappedComponent, {
      ...props,
      insuranceEligibility,
    });
  };

  WithInsuranceEligibility.propTypes = {
    passengersForm: PropTypes.object,
    purchaserForm: PropTypes.object,
    purchaser: PropTypes.object,
    purchase: PropTypes.object,
  };

  WithInsuranceEligibility.displayName = `WithInsuranceEligibility(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithInsuranceEligibility;
};

export default useInsuranceEligibility;
